// src/components/public/verifyLogin.tsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LogoIcon from "../../assets/svg/logoIcon";
import { useAuth } from "../../context/authContext";
import createAxiosInstance from "../../utils/axiosConfig";
import { verify_mfa_token } from "../../utils/endpoints";
import Alert from "../common/alerts/alert";
import Footer from "../common/footer";

const VerifyLogin: React.FC = () => {
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [verifying, setVerifying] = useState(true);
  const [alertProps, setAlertProps] = useState({
    show: false,
    type: "danger" as "danger" | "success",
    message: ""
  });

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get("token");
        
        if (!token) {
          setAlertProps({
            show: true,
            type: "danger",
            message: "No verification token found in URL."
          });
          setVerifying(false);
          return;
        }
        
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(verify_mfa_token, { mfa_token: token });
        
        if (response.data && response.data.access_token) {
          // User verified, log them in
          login(
            response.data.id, 
            response.data.access_token, 
            response.data
          );
          navigate("/chat");
        } else {
          setAlertProps({
            show: true,
            type: "danger",
            message: `Error: ${response.data.message || response.data.detail}`
          });
          setVerifying(false);
        }
      } catch (err: any) {
        console.error("Verification error:", err);
        setAlertProps({
          show: true,
          type: "danger",
          message: err.response?.data?.message || err.response?.data?.detail || "An error occurred during verification."
        });
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [location, login, navigate]);
  
  return (
    <section className="authentication h-100">
      <div className="row align-items-center justify-content-center min-vh-96 w-100">
        <div className="col-md-10 col-lg-9 col-xl-6">
          <div className="card mx-auto text-center">
            <div className="card-body">
              <div className="mb-3">
                <LogoIcon />
              </div>
              <h2 className="my-3 my-md-4">Verifying Login</h2>
              
              {verifying && (
                <div className="text-center my-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Verifying your login...</p>
                </div>
              )}
              
              {alertProps.show && (
                <Alert 
                  message={alertProps.message} 
                  type={alertProps.type} 
                  show={alertProps.show} 
                  onClose={() => setAlertProps({...alertProps, show: false})} 
                />
              )}
              
              {!verifying && (
                <div className="mt-4">
                  <a href="/login" className="btn btn-primary">
                    Return to Login
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </section>
  );
};

export default VerifyLogin;